import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, LogOut } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { FileImage } from "@/components/FileImage";
import { StatusBadge } from "@/components/StatusBadge";

export default function Profile() {
  const { user, setUser, t, logout, lang, formatError } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    phone: user.phone || "", country: user.country || "", city: user.city || "",
    preferred_city: user.preferred_city || "", introduction: user.introduction || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data.user);
      toast.success(t("saved"));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const signOut = async () => { await logout(); navigate("/", { replace: true }); };
  const fmt = (d) => d ? new Date(d).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const inputCls = "cu-input w-full px-4 py-3 text-sm";

  return (
    <div className="px-6 pt-8 fade-up">
      <h1 className="font-serif-cu text-2xl text-center mb-8">{t("profile")}</h1>

      <div className="flex flex-col items-center mb-8">
        <div className="w-24 h-24 rounded-full overflow-hidden gold-border mb-4">
          <FileImage path={user.profile_photo_path} alt="" className="w-full h-full object-cover" fallback={<div className="w-full h-full flex items-center justify-center bg-[rgba(212,175,55,0.07)]"><User size={32} className="text-[color:var(--cu-gold)]" /></div>} />
        </div>
        <h2 className="font-serif-cu text-xl">{user.first_name} {user.surname}</h2>
        <p className="text-xs text-[color:var(--cu-muted)] mt-1">{user.email}</p>
        <div className="mt-3"><StatusBadge status={user.status} /></div>
      </div>

      <div className="cu-card p-5 mb-6">
        <h3 className="eyebrow mb-4">{t("membership_details")}</h3>
        <div className="space-y-3 text-sm">
          <Row label={t("member_no")} value={user.membership_number || "—"} mono />
          <Row label={t("joining_date")} value={fmt(user.join_date)} />
          <Row label={t("renewal_date")} value={fmt(user.renewal_date)} />
        </div>
      </div>

      <div className="cu-card p-5 mb-6">
        <h3 className="eyebrow mb-4">{t("contact_information")}</h3>
        <div className="space-y-4">
          <div><label className="eyebrow block mb-2">{t("phone")}</label><input data-testid="profile-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="eyebrow block mb-2">{t("country")}</label><input data-testid="profile-country-input" value={form.country} onChange={set("country")} className={inputCls} /></div>
            <div><label className="eyebrow block mb-2">{t("city")}</label><input data-testid="profile-city-input" value={form.city} onChange={set("city")} className={inputCls} /></div>
          </div>
          <div><label className="eyebrow block mb-2">{t("introduction")}</label><textarea data-testid="profile-introduction-input" value={form.introduction} onChange={set("introduction")} rows={3} className={`${inputCls} resize-none`} /></div>
          <button onClick={save} disabled={saving} data-testid="profile-save-button" className="btn-gold w-full py-3 text-xs">{t("save_changes")}</button>
        </div>
      </div>

      <button onClick={signOut} data-testid="profile-signout-button" className="btn-ghost w-full py-3.5 text-xs flex items-center justify-center gap-2 mb-6">
        <LogOut size={15} /> {t("sign_out")}
      </button>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[color:var(--cu-border)] last:border-0 pb-2.5 last:pb-0">
      <span className="eyebrow">{label}</span>
      <span className={`text-right text-[color:var(--cu-text)] ${mono ? "font-mono-cu gold-text" : ""}`}>{value}</span>
    </div>
  );
}
